import React, { useMemo } from "react";
import { AnalysisResult } from "../../contracts/scoring";
import { MicroTimingVisualizer } from "../../components/MicroTimingVisualizer";
import { getTimingToleranceMs } from "../../services/severityEngine";
import { AdviceCard } from "../../components/SmartTrainer/AdviceCard";

type MetricCard = { label: string; value: string; hint?: string };
type PeakLike = { time: number; intensity?: number };

// ✅ Adaptation stricte au contrat attendu par MicroTimingVisualizer
// IMPORTANT : ton MicroTimingVisualizer lit des champs (t, v) (ou équivalent).
// Ici on aligne explicitement pour éviter un rendu “vide” malgré des données présentes.
type VisualizerPeak = { t: number; v?: number };

const safeNum = (v: any): number | null => {
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

const meanAbs = (arr: number[]) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const nums = arr.map(Number).filter(Number.isFinite);
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + Math.abs(b), 0) / nums.length;
};

const NoDataPanel: React.FC<{ reason: string }> = ({ reason }) => (
  <div className="bg-white/[0.02] border border-white/10 rounded-[48px] p-10 text-center">
    <div className="text-3xl mb-4">🔒</div>
    <div className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-500">
      NO=100% — DONNÉE DSP ABSENTE
    </div>
    <div className="mt-3 text-white font-black text-xl">Finesse verrouillée</div>
    <div className="mt-3 text-slate-300 text-sm italic">{reason}</div>
    <div className="mt-6 text-[10px] text-slate-500">
      Mode strict : aucun fallback, aucune valeur par défaut, aucune reconstruction.
    </div>
  </div>
);

export const FinesseTab: React.FC<{
  result: AnalysisResult;
  currentLevelId: number;
}> = ({ result, currentLevelId }) => {
  const dbg: any = (result as any)?.session?.metrics?.debug;
  if (!dbg) return <NoDataPanel reason="session.metrics.debug absent." />;

  /* 1) Stability — runtime only */
  const stability = safeNum(dbg.stability);
  if (stability === null) {
    return <NoDataPanel reason="dbg.stability absent ou invalide." />;
  }

  /* 2) timingDeviationMs — runtime only */
  const rawDeviations = dbg.timingDeviationMs;
  if (!Array.isArray(rawDeviations)) {
    return <NoDataPanel reason="dbg.timingDeviationMs absent." />;
  }

  const devNums = rawDeviations.map(safeNum).filter((v): v is number => v !== null);
  if (devNums.length < 4) {
    return <NoDataPanel reason="dbg.timingDeviationMs insuffisant (<4 points valides)." />;
  }

  /* 3) Peaks — UNIQUEMENT stuPeaks runtime */
  const rawPeaks = (result as any)?.rhythm?.stuPeaks;
  if (!Array.isArray(rawPeaks) || rawPeaks.length < 4) {
    return <NoDataPanel reason="result.rhythm.stuPeaks absent ou insuffisant (<4 pics)." />;
  }

  const peaks: PeakLike[] = rawPeaks
    .map((p: any) => {
      const t = safeNum(p?.time);
      if (t === null) return null;
      const intensity = safeNum(p?.intensity);
      return { time: t, intensity: intensity ?? undefined };
    })
    .filter(Boolean) as PeakLike[];

  if (peaks.length < 4) {
    return <NoDataPanel reason="stuPeaks présents mais non exploitables (time/intensity invalides)." />;
  }

  /* ✅ Adapter strict au visualizer (évite rendu vide si keys attendues ≠ time/intensity) */
  const visualizerPeaks: VisualizerPeak[] = useMemo(() => {
    return peaks.map((p) => ({
      t: p.time, // time en secondes (comme dans ton MicroTimingVisualizer actuel)
      v: p.intensity,
    }));
  }, [peaks]);

  /* 4) BPM — runtime only */
  const bpm = safeNum((result as any)?.session?.bpm);
  if (bpm === null) {
    return <NoDataPanel reason="session.bpm absent ou invalide." />;
  }

  /* 5) Duration — runtime only */
  const durationSec = safeNum((result as any)?.session?.duration);
  if (durationSec === null) {
    return <NoDataPanel reason="session.duration absent ou invalide." />;
  }

  /* 6) Tolérance — fonction pure */
  const toleranceMs = getTimingToleranceMs(currentLevelId, bpm);

  /* 7) Score — dérivé direct, non clampé */
  const stabilityPct = useMemo(() => Math.round(stability * 100), [stability]);

  const metrics: MetricCard[] = useMemo(() => {
    const m = meanAbs(devNums);
    return [
      { label: "Stability", value: `${stabilityPct}%`, hint: "dbg.stability (runtime)" },
      {
        label: "Mean |deviation|",
        value: m === null ? "—" : `${Math.round(m)}ms`,
        hint: "dbg.timingDeviationMs (runtime)",
      },
      { label: "Tolerance", value: `${Math.round(toleranceMs)}ms`, hint: "level + bpm" },
      { label: "Samples", value: String(devNums.length), hint: "points DSP" },
      { label: "Peaks", value: String(peaks.length), hint: "stuPeaks runtime" },
      { label: "Duration", value: `${Math.round(durationSec)}s`, hint: "session.duration" },
    ];
  }, [devNums, peaks.length, stabilityPct, toleranceMs, durationSec]);

  const diagnostics =
    (result as any)?.feedback?.diagnostics?.filter((d: any) =>
      ["STABILITY", "SYNCHRONIZATION"].includes(String(d?.domain || "").toUpperCase())
    ) || [];

  const scoreColor = stabilityPct >= 80 ? "text-emerald-400" : "text-smart-gold";

  return (
    <div className="flex flex-col gap-10 animate-fade-in pb-12">
      {/* SCORE */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center gap-6">
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">
            Micro-Timing Stability
          </span>

          <div className="flex items-baseline gap-2">
            <span className={`text-8xl font-black italic tracking-tighter ${scoreColor}`}>
              {stabilityPct}
            </span>
            <span className="text-xl font-bold text-slate-700">/100</span>
          </div>
        </div>

        <div className="lg:col-span-8 bg-black/40 p-8 rounded-[48px] border border-white/10 flex flex-col gap-6">
          <p className="text-sm text-slate-300 italic leading-relaxed">
            Stabilité micro-tempo calculée à partir des pics d’attaque réels (stuPeaks) et des écarts temporels DSP
            (timingDeviationMs). Mode strict : aucune métrique absente n’est substituée.
          </p>

          <div className="flex gap-3">
            <div className="px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-[10px] font-black text-cyan-400 uppercase">
              ±{Math.round(toleranceMs)}ms
            </div>
            <div className="px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-black text-emerald-400 uppercase">
              DSP only
            </div>
          </div>
        </div>
      </div>

      {/* METRICS + ADVICE */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {metrics.map((m, i) => (
              <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
                <div className="text-[9px] font-black uppercase text-slate-500">{m.label}</div>
                <div className="text-2xl font-black italic text-white">{m.value}</div>
                {m.hint && <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-4">
            {diagnostics.length > 0 ? (
              diagnostics.map((adv: any, i: number) => (
                <AdviceCard key={i} advice={adv} isHighContrast={false} />
              ))
            ) : (
              <div className="text-center opacity-40">
                <p className="text-xs font-bold uppercase text-slate-500">Stabilité validée</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* VISUAL */}
      <MicroTimingVisualizer
        peaks={visualizerPeaks as any}
        deviations={devNums}
        toleranceMs={toleranceMs}
        duration={durationSec}
        levelId={currentLevelId}
      />
    </div>
  );
};
